import { useState, useCallback } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import {
  invalidateCache,
  invalidateResultCaches,
  PULSE_CACHE_KEYS,
} from './cache';

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Deletes a UI-managed Pulse check definition. */
export const useDeleteCheck = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(
    async (checkId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `plugin://pulse/checks/${encodeURIComponent(checkId)}`,
          { method: 'DELETE' },
        );
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Failed to delete check: ${response.status}`,
          );
        }
        invalidateCache(PULSE_CACHE_KEYS.checks);
        invalidateResultCaches(checkId);
      } catch (error_) {
        const err = error_ as Error;
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { remove, loading, error };
};
